// TODO: Replace superagent with fetch
// (a minimal library is ok too, like ofetch)

import { isEmpty, camelCase, snakeCase } from "lodash-es";
import { v4 as uuidv4 } from "uuid";
import type {
  Response as SuperagentResponse,
  ResponseError,
  Request as SuperagentRequest
} from "superagent";

import superagent from "superagent";
import envVars from "@/helpers/Env";
import eventHub from "@/helpers/utility/Events";
import { i18n } from "@/i18n";

import {
  NoRefreshTokenError,
  NoInternetError,
  ServerError,
  BadRequestError,
  AccessDeniedError,
  InvalidTokenError,
  NotFoundError,
  NoTokenError,
  ConflictError,
  WrongPasswordError,
  LicenseLimitReachedError,
  ModelLimitReachedError
} from "@core/errors/Errors.js";
import { transformKeys } from "../helpers/utility/CollectionFunctions";
import { unref } from "vue";
import { wait } from "@/helpers/utility/Timing";

type MultipartValueSingle = Blob | File | Buffer | string | boolean | number;

export type AuthData = {
  "accessToken": string;
  "refreshToken": string;
  "expiresIn": number;
  "expiresAt": string | number;
};

interface RequiredRequestOptions<T> {
  /**
   * Set this to false on auth requests.
   */
  sendAuthToken: boolean;

  retryOnExpiredToken: boolean;
  
  /**
   * The minimum time the request should take, in milliseconds.
   * (Some requests feel better if they take a bit longer)
  **/
  minTime: number;

  query: { [key: string]: string | number };
  headers: { [key: string]: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: object;
  responseType: string;
  attach: { [key: string]: { data: MultipartValueSingle; fileName: string } };
  toSnakeCase: boolean;
  showLoginPopup: boolean;
  parseResponse: parseResponseFunction<T>;
  synch: syncFunction<T>;
  progressTracker: {
    [requestId: string]: { percent?: number; total?: number };
  };
  params?: object;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RequestOptions<T = any> = Partial<RequiredRequestOptions<T>>;

type parseResponseFunction<T> = (
  response: SuperagentResponse,
  requestOptions: RequestOptions<T>
) => T;

type syncFunction<T> = (
  request: (additionalOptions?: RequestOptions<T>) => Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestOptions?: any
) => Promise<T>;

type HTTPMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

// ==============
// === TOKENS ===

let _expirationTimeout: number | null | NodeJS.Timeout = null;
let tokenExpiresAt: number | null = null; // timestamp
let _token = getLastToken() ?? null;
let _refreshToken = getLastRefreshToken() ?? null;

export function setToken(value: string | null) {
  _token = value;
  if (localStorage) {
    if (value) localStorage.setItem("token", value);
    else localStorage.removeItem("token");
  }
}
export function getToken() {
  return _token;
}

export function setRefreshToken(value: string | null) {
  _refreshToken = value;

  if (localStorage) {
    if (value) localStorage.setItem("refresh_token", value);
    else localStorage.removeItem("refresh_token");
  }
}
export function getRefreshToken() {
  return _refreshToken;
}

export function isTokenExpired() {
  if (tokenExpiresAt == null) return true;
  return Date.now() < tokenExpiresAt;
}

function getLastToken() {
  if (!localStorage) return null;
  return localStorage.getItem("token");
}
function getLastRefreshToken() {
  if (!localStorage) return null;
  return localStorage.getItem("refresh_token");
}

// ================
// === REQUESTS ===

export function get<T = never>(url: string, options: RequestOptions<T> = {}) {
  return sendRequest<T>("GET", url, options);
}

export function post<T = never>(url: string, options: RequestOptions<T> = {}) {
  return sendRequest<T>("POST", url, options);
}

export function destroy<T = never>(
  url: string,
  options: RequestOptions<T> = {}
) {
  return sendRequest<T>("DELETE", url, options);
}

export function put<T>(url: string, options: RequestOptions<T> = {}) {
  return sendRequest<T>("PUT", url, options);
}

export function patch<T>(url: string, options: RequestOptions<T> = {}) {
  return sendRequest<T>("PATCH", url, options);
}

export async function sendRequest<T = SuperagentResponse>(
  method: HTTPMethod,
  url: string,
  options: RequestOptions<T> = {}
) {
  if (envVars.saveLocally && options.synch) {
    return options.synch((additionalOptions = {}) => {
      return makeRequest<T>(method, url, {
        ...options,
        ...additionalOptions
      });
    }, options);
  } else {
    return await makeRequest<T>(method, url, options);
  }
}

/**
 * Send a request to the API.
 * Retries by default.
 */
export async function makeRequest<T = SuperagentResponse>(
  method: HTTPMethod,
  url: string,
  options: RequestOptions<T> = {}
): Promise<T> {
  const {
    sendAuthToken = true,
    retryOnExpiredToken = true,
    showLoginPopup = true,
    parseResponse = standardCleanResponse
  } = options;

  const request = createRequest<T>(method, url, options);

  try {
    if (sendAuthToken && getToken() == null) {
      console.error("trying to call", url, "without token");
      throw new NoTokenError();
    }

    const waitedResponse = await Promise.all([
      wait(options.minTime || 0),
      request
    ]);

    const response = waitedResponse[1];

    // transform to camelcase
    if (response.body && typeof response.body == "object") {
      response.body = camelCaseDeep(response.body);
    }

    return parseResponse(response, options);
  } catch (_error) {
    const error = _error as ResponseError & {
      request: SuperagentRequest;
      url: string;
    };

    const body = error?.response?.body;

    // setup message:
    let message = body ? body?.message : error.message;
    if (message == null && typeof body === "string") message = body;

    // setup code
    const code = body?.code;

    if (error.status != null) {
      console.error(error.request, error.status, error.response?.body, message);
    } else if (error.url) {
      error.status = undefined;
    }

    switch (error.status) {
      case 400: {
        const fields: { [key: string | number]: string } = {};
        if (error?.response?.body && Array.isArray(error?.response?.body)) {
          for (let i = 0; i < error.response.body.length; i++) {
            const e = error.response.body[i];
            if (isEmpty(e)) continue;
            fields[i] = e?.message || e?.non_field_errors?.[0]?.message || e;
          }
          message = null;
        } else if (typeof error?.response?.body === "object") {
          for (const key in error.response.body) {
            fields[key] =
              error.response.body[key]?.message ||
              error.response.body[key][0]?.message ||
              error.response.body[key];
          }
        }
        throw new BadRequestError(message || undefined, code, fields);
      }

      case 401: {
        setToken(null);
        if (sendAuthToken && retryOnExpiredToken) {
          await getNewToken(showLoginPopup);
          const retryRequest = makeRequest<T>(method, url, {
            ...options,
            retryOnExpiredToken: false
          });
          console.log("Retrying with new token...");
          return await retryRequest;
        } else throw new InvalidTokenError();
      }

      case 403: {
        if (code === "license_limit_reached") {
          throw new LicenseLimitReachedError(message, code, body.limits);
        }
        if (code === "model_limit_reached") {
          throw new ModelLimitReachedError(body.limits.models);
        }
        if (code === "wrong_password") {
          throw new WrongPasswordError(message);
        }
        throw new AccessDeniedError(message, code);
      }

      case 404: {
        throw new NotFoundError(message);
      }

      case 409: {
        throw new ConflictError(message, code, body.conflicting_entry);
      }

      case 500: {
        throw new ServerError(message, code);
      }

      case null:
      case undefined: {
        if (error.url) {
          console.warn("no internet", error);
          const errorMessage = i18n.t("errors.noInternet");
          throw new NoInternetError(errorMessage);
        }
      }
    }
    throw error;
  }
}

function standardCleanResponse(response: SuperagentResponse) {
  if (response.xhr.responseType === "blob") {
    return response.xhr.response;
  }

  const body = response.body;

  if (Array.isArray(body?.results)) {
    return body.results;
  }
  return body;
}

export function createRequest<T>(
  method: HTTPMethod,
  url: string,
  requestOptions: RequestOptions<T> = {}
) {
  const {
    query = null,
    data = null,
    headers = {},
    attach = null,
    sendAuthToken = true,
    toSnakeCase = true,
    responseType = "json",
    progressTracker = null
  } = requestOptions;

  const prefixedUrl = formatRequestURL(url);

  const request = superagent(method.toUpperCase(), prefixedUrl);
  request.set("Accept", "application/json");
  request.set("Accept-Language", `${unref(i18n.locale)};q=0.9, *;q=0.5`);

  request.responseType(responseType);

  if (query) {
    request.query(query);
  }

  if (data) {
    let dataToSend = data;
    if (toSnakeCase) {
      if (data && typeof data == "object") {
        dataToSend = snakeCaseDeep(data);
      }
    }

    // if we're sending a file too, add the data
    // to the multipart-form-request instead of
    // sending it as JSON.
    if (attach) {
      // add every bit of data as a
      // multipart-formdata field.
      for (const key in dataToSend) {
        const dataValue = dataToSend[key as keyof typeof dataToSend];
        if (dataValue == null) {
          request.field(key, "");
        } else {
          request.field(key, dataValue);
        }
      }
    } else {
      // If were NOT sending any file,
      // transfer the data as JSON.
      request.send(dataToSend);
    }
  }

  if (attach) {
    for (const key in attach) {
      const entry = attach[key];
      request.attach(key, entry.data as superagent.MultipartValueSingle, entry.fileName);
    }
  }

  if (progressTracker) {
    const requestId = uuidv4();
    progressTracker[requestId] = {
      percent: 0,
      total: undefined
    };
    request.on("progress", (event) => {
      progressTracker[requestId] = event;
    });
  }

  for (const headerKey in headers) {
    request.set(headerKey, headers[headerKey]);
  }

  // We only send the auth header if we have an access token.
  if (sendAuthToken && getToken() != null) {
    request.set("Authorization", `Bearer ${getToken()}`);
  }

  return request;
}

/**
 * Add protocol, host and "/api" prefix to url if needed.
 */
function formatRequestURL(url: string) {
  let prefixedUrl = "";
  if (url.startsWith("http:") || url.startsWith("https:")) {
    prefixedUrl = url;
  } else {
    prefixedUrl = url.startsWith("/api") ? url : "/api" + url;
    prefixedUrl = `${envVars.platformUrl}` + prefixedUrl;
  }
  return prefixedUrl;
}

/**
 * Get a new access token from the web platform
 */
export async function getNewToken(showLoginPopup = true) {
  try {
    if (getRefreshToken() == null) {
      throw new NoRefreshTokenError();
    }

    const authData = await post<AuthData>("/auth/", {
      sendAuthToken: false,
      data: {
        grant_type: "refresh_token",
        refresh_token: getRefreshToken()
      }
    });

    if (!authData.accessToken) {
      throw new Error("No access token received.");
    }

    saveToken(authData);

    return getToken();
  } catch (error) {
    if (error instanceof NoTokenError) {
      setRefreshToken(null);
    }

    // if we've got saved credentials, use those to retry:
    if (showLoginPopup && window.electron) {
      const encryptedCredentials = localStorage.getItem("login");
      if (encryptedCredentials) {
        const loginDataString = await window.electron.decryptCredential(
          encryptedCredentials
        );
        const loginData = loginDataString ? JSON.parse(loginDataString) : null;
        if (loginData && loginData.email) {
          try {
            const authResponse = await post("/auth/", {
              sendAuthToken: false,
              data: {
                grant_type: "password",
                username: loginData.email,
                password: loginData.password
              }
            });
            saveToken(authResponse);
          } catch (__error) {
            const error = __error as { status?: number };
            if (error.status == 401 || error.status == 400) {
              localStorage.removeItem("login");
            }
            throw error;
          }
        }
      }
    }

    // show a login popup
    if (
      showLoginPopup &&
      (error instanceof InvalidTokenError || error instanceof NoTokenError)
    ) {
      return await waitForLogin();
    }

    if (error instanceof InvalidTokenError) {
      setRefreshToken(null);
      throw new InvalidTokenError("Refresh token expired.");
    }
    throw error;
  }
}

/**
 * Save the token AND set a timeout to refresh it when expired.
 */
export function saveToken(response: { 
  "accessToken": string;
  "refreshToken": string;
  "expiresIn": number;
  "expiresAt": number | string;
}) {
  // Save token
  setToken(response.accessToken);

  if (response.refreshToken) {
    setRefreshToken(response.refreshToken);
  }

  // Refresh the token when it expires.
  const FIVE_MINUTES = 5 * 60 * 1000;
  const expireHeader =
    response.expiresAt ||
    Date.now() + FIVE_MINUTES;
  const expiresAt = new Date(expireHeader).getTime();
  tokenExpiresAt = expiresAt;
  const refreshInMs = expiresAt - Date.now();
  if (_expirationTimeout != null) {
    clearTimeout(_expirationTimeout);
  }
  _expirationTimeout = setTimeout(async () => {
    try {
      getNewToken(true);
    } catch (_error) {
      const error = _error as { status: number; message: string };
      console.warn("Token refreshing failed.", error.status, error.message);
    }
  }, refreshInMs);
}

function waitForLogin() {
  eventHub.emit("request-alert-resolve");
  return new Promise((resolve, reject) => {
    eventHub.once("login", <T>(user: T) => {
      resolve(user);
    });

    eventHub.once("login-aborted", () => {
      reject(new Error("Not logged in."));
    });

    eventHub.emit("request-login");
  });
}

const camelCaseDeep = transformKeys.bind(null, (str: string) => camelCase(str));
const snakeCaseDeep = transformKeys.bind(null, (str: string) => snakeCase(str));
